import { useEffect, useState } from "react";
import { Check, Construction, Eye, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { api, apiError } from "@/lib/api";

const Row = ({ curtain, onSaved, busy, setBusy }) => {
  const [open, setOpen] = useState(curtain.open);
  const [message, setMessage] = useState(curtain.message);
  const [preview, setPreview] = useState((curtain.preview || []).join(", "));

  useEffect(() => {
    setOpen(curtain.open);
    setMessage(curtain.message);
    setPreview((curtain.preview || []).join(", "));
  }, [curtain]);

  const dirty =
    open !== curtain.open ||
    message !== curtain.message ||
    preview !== (curtain.preview || []).join(", ");

  // Nothing is written until Apply — so the wording and the named officers land with the curtain,
  // never a moment after it, with officers reading the generic text in between.
  const apply = async () => {
    setBusy(true);
    try {
      await api.put(`/cockpit/curtain/${curtain.screen}`, {
        open,
        message,
        preview: preview
          .split(",")
          .map((e) => e.trim())
          .filter(Boolean),
      });
      toast.success(
        open
          ? `${curtain.label} is open to everyone whose tab shows it.`
          : `${curtain.label} now says "coming soon".`,
      );
      await onSaved();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className={`border-b border-border last:border-b-0 ${dirty ? "bg-primary/5" : ""}`}
      data-testid={`curtain-${curtain.screen}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold">{curtain.label}</span>
            {!curtain.open && (
              <span className="font-data border border-[hsl(var(--watch))] px-1 text-[9px] uppercase tracking-wider text-[hsl(var(--watch))]">
                coming soon
              </span>
            )}
            {dirty && (
              <span className="font-data border border-primary px-1 text-[9px] uppercase tracking-wider text-primary">
                not applied
              </span>
            )}
          </div>
          <div className="font-data mt-0.5 text-[11px] text-muted-foreground">
            {curtain.screen}
            {curtain.preview?.length
              ? ` · ${curtain.preview.length} officer(s) may look behind`
              : ""}
          </div>
        </div>
        <label className="flex items-center gap-2 text-[11px]">
          {open ? (
            <Check className="h-3.5 w-3.5 text-primary" />
          ) : (
            <Construction className="h-3.5 w-3.5 text-[hsl(var(--watch))]" />
          )}
          <Switch
            checked={open}
            disabled={busy}
            onCheckedChange={setOpen}
            data-testid={`curtain-switch-${curtain.screen}`}
          />
          <span className="font-data uppercase tracking-wider text-muted-foreground">
            {open ? "open to all" : "curtain down"}
          </span>
        </label>
      </div>

      {(!open || dirty) && (
        <div className="space-y-3 border-l-2 border-primary bg-muted/40 px-4 py-3">
          {!open && (
            <>
              <div>
                <div className="label-caps">What the officer is told</div>
                <Input
                  data-testid={`curtain-message-${curtain.screen}`}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="mt-1.5 h-9 rounded-none bg-background text-xs"
                />
              </div>
              <div>
                <div className="label-caps flex items-center gap-1.5">
                  <Eye className="h-3 w-3" /> Who may look behind it — e-mails, comma separated
                </div>
                <Input
                  data-testid={`curtain-preview-${curtain.screen}`}
                  value={preview}
                  placeholder="nobody but you, unless you name someone"
                  onChange={(e) => setPreview(e.target.value)}
                  className="mt-1.5 h-9 rounded-none bg-background text-xs"
                />
              </div>
            </>
          )}
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              className="rounded-none"
              disabled={busy || !dirty}
              onClick={apply}
              data-testid={`curtain-apply-${curtain.screen}`}
            >
              {busy ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : null}
              {dirty ? "Apply" : "Applied"}
            </Button>
            {dirty && (
              <Button
                size="sm"
                variant="ghost"
                className="rounded-none text-[11px]"
                onClick={() => {
                  setOpen(curtain.open);
                  setMessage(curtain.message);
                  setPreview((curtain.preview || []).join(", "));
                }}
                data-testid={`curtain-cancel-${curtain.screen}`}
              >
                Discard
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/** Every tab that can go behind a "coming soon" curtain, and whether it is behind one. */
export const CockpitCurtains = ({ curtains, onSaved }) => {
  const [busy, setBusy] = useState(false);
  const down = (curtains || []).filter((c) => !c.open);

  return (
    <div className="border border-border bg-card" data-testid="cockpit-curtains">
      <div className="border-b border-border p-4">
        <div className="label-caps flex items-center gap-2 text-primary">
          <Construction className="h-3.5 w-3.5" /> Curtains
        </div>
        <p className="mt-1.5 max-w-3xl text-[11px] leading-relaxed text-muted-foreground">
          A tab behind a curtain stays in the officer's menu — so they know it is coming — but says
          "coming soon" instead of opening, and the portal hands out none of its data either. Name
          an officer under it and they see the real thing while everyone else waits. Move the
          switch, write what the officer should be told, then press Apply — nothing changes until
          you do. Home and My profile have no curtain; nobody can be shut out of those.
          {down.length ? (
            <b className="ml-1 text-[hsl(var(--watch))]">
              {down.length} tab(s) behind a curtain right now.
            </b>
          ) : (
            <b className="ml-1">Every tab is open.</b>
          )}
        </p>
      </div>
      {(curtains || []).map((curtain) => (
        <Row
          key={curtain.screen}
          curtain={curtain}
          onSaved={onSaved}
          busy={busy}
          setBusy={setBusy}
        />
      ))}
    </div>
  );
};
